import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { DialogFooter } from "@/components/ui/dialog"
import { Loader2, Clock, Users, Info, Plus, Trash2 } from "lucide-react"
import { Shift, DayGroup, DayNumber } from "@/types"
import { useShiftForm } from "./useShiftForm"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { DayGroupSelector } from "@/components/ui/DayGroupSelector"
import { getAvailableDays, validateDayGroups } from "@/utils/shiftFormUtils"

interface ShiftDialogFormProps {
  shift: Shift | null
  onSave: (shift: Shift, isNew: boolean) => void
  onCancel: () => void
  existingShifts: Shift[]
}

export function ShiftDialogForm({ 
  shift, 
  onSave, 
  onCancel,
  existingShifts
}: ShiftDialogFormProps) {
  const { 
    category, 
    setCategory, 
    shiftName, 
    setShiftName, 
    validFrom,
    setValidFrom,
    inWorkingHours,
    setInWorkingHours,
    dayGroups,
    setDayGroups,
    isSaving, 
    saveShift 
  } = useShiftForm(shift, onSave)
  
  const [customCategory, setCustomCategory] = useState("")
  const [showCustomCategory, setShowCustomCategory] = useState(false)
  
  // Get unique categories from existing shifts
  const existingCategories = Array.from(new Set(existingShifts.map(s => s.name).filter(Boolean)))
  
  // Reset form when shift changes
  useEffect(() => {
    if (shift) {
      setCategory(shift.name || "")
      setShiftName(shift.code || "")
      setValidFrom(shift.valid_from || "")
      setShowCustomCategory(false)
      setCustomCategory("")
    } else {
      setCategory("")
      setShiftName("")
      setValidFrom("")
      setShowCustomCategory(false)
      setCustomCategory("")
    }
  }, [shift, setCategory, setShiftName, setValidFrom])

  // Get current validation state for disabled button
  const getCurrentValidation = () => {
    return validateDayGroups(dayGroups)
  }
  
  const handleCategoryChange = (value: string) => {
    if (value === "custom") {
      setShowCustomCategory(true)
      setCategory("")
    } else {
      setShowCustomCategory(false)
      setCategory(value)
      setCustomCategory("")
    }
  }
  
  const handleCustomCategoryChange = (value: string) => {
    setCustomCategory(value)
    setCategory(value)
  }

  // Day group management functions
  const addDayGroup = () => {
    const availableDays = getAvailableDays(dayGroups)
    if (availableDays.length === 0) return

    const newGroup: DayGroup = {
      days: [],
      start: '',
      end: '',
      breakHours: { hours: 0, minutes: 0 }
    }

    setDayGroups([...dayGroups, newGroup])
  }

  const removeDayGroup = (index: number) => {
    if (dayGroups.length === 1) return // Keep at least one group
    setDayGroups(dayGroups.filter((_, i) => i !== index))
  }

  const updateDayGroup = (index: number, updates: Partial<DayGroup>) => {
    const newGroups = [...dayGroups]
    newGroups[index] = { ...newGroups[index], ...updates }
    setDayGroups(newGroups)
  }

  const canAddGroup = getAvailableDays(dayGroups).length > 0


  
  return (
    <>
      <div className="space-y-4 py-4 px-4">
        {/* Basic Information */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Users className="h-4 w-4" />
            Basic Information
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-medium">Category</Label>
              {showCustomCategory ? (
                <Input 
                  id="custom-category" 
                  value={customCategory} 
                  onChange={(e) => handleCustomCategoryChange(e.target.value)}
                  placeholder="Enter custom category"
                  className="h-9"
                />
              ) : (
                <Select value={category} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {existingCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">+ Add Custom Category</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="shift-name" className="text-sm font-medium">Shift Name</Label>
              {!category ? (
                <div className="text-xs text-muted-foreground p-2 border rounded-md bg-muted/30 h-9 flex items-center">
                  Select category first
                </div>
              ) : (
                <Input 
                  id="shift-name" 
                  value={shiftName} 
                  onChange={(e) => setShiftName(e.target.value)}
                  placeholder="Enter shift name (e.g., N1, ES, MS)"
                  className="h-9"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="valid-from" className="text-sm font-medium">Valid From</Label>
              <Input 
                id="valid-from" 
                type="date" 
                value={validFrom} 
                onChange={(e) => setValidFrom(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="h-9 date-input"
              />
            </div>
          </div>
        </div>

        {/* Overtime Hours Policy */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Users className="h-4 w-4" />
            Overtime Hours Policy
            <TooltipProvider delayDuration={25}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs whitespace-normal px-4 py-2">
                  Set whether overtime is allowed within shift hours or must be outside shift hours.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          <RadioGroup 
            value={inWorkingHours ? "within" : "outside"} 
            onValueChange={(value) => setInWorkingHours(value === "within")}
            className="flex flex-row space-x-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="within" id="within" />
              <Label htmlFor="within" className="text-sm font-normal cursor-pointer">
                <span className="font-normal">Within Shift Hours</span>
                <span className="text-muted-foreground text-xs font-light block">OT between shift hours</span>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="outside" id="outside" />
              <Label htmlFor="outside" className="text-sm font-normal cursor-pointer">
                <span className="font-normal">Outside Shift Hours</span>
                <span className="text-muted-foreground text-xs font-light block">OT outside shift hours</span>
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Day Groups */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Clock className="h-4 w-4" />
              Schedule Groups
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addDayGroup}
              disabled={!canAddGroup}
              className="h-8"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Day Group
            </Button>
          </div>

          <div className="space-y-3">
            {dayGroups.map((group, index) => {
              const availableDays = [...getAvailableDays(dayGroups), ...group.days].sort() as DayNumber[]
              
              return (
                <Card key={index} className="border-border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">
                        Day Group {index + 1}
                        {index === 0 && dayGroups.length === 1 && " (Default: Mon-Fri)"}
                      </CardTitle>
                      {dayGroups.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDayGroup(index)}
                          className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Day Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Days</Label>
                      <DayGroupSelector
                        selectedDays={group.days}
                        availableDays={availableDays}
                        onChange={(days) => updateDayGroup(index, { days })}
                      />
                      <p className="text-xs text-muted-foreground">
                        {group.days.length === 0 
                          ? "Select at least one day"
                          : `${group.days.length} day${group.days.length !== 1 ? 's' : ''} selected`
                        }
                      </p>
                    </div>

                    {/* Time Configuration */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`start-time-${index}`} className="text-sm font-medium">Start Time</Label>
                        <Input 
                          id={`start-time-${index}`}
                          type="time" 
                          value={group.start} 
                          onChange={(e) => updateDayGroup(index, { start: e.target.value })}
                          className="h-9 w-full"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor={`end-time-${index}`} className="text-sm font-medium">End Time</Label>
                        <Input 
                          id={`end-time-${index}`}
                          type="time" 
                          value={group.end} 
                          onChange={(e) => updateDayGroup(index, { end: e.target.value })}
                          className="h-9 w-full"
                        />
                      </div>
                      
                      <div className="col-span-2 space-y-2">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm font-medium">Break Time</Label>
                          <TooltipProvider delayDuration={25}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs whitespace-normal px-4 py-2">
                                Break time applies only if total OT hours ≥ 8
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="flex gap-1">
                          <div className="flex-1">
                            <Select 
                              value={group.breakHours.hours.toString()} 
                              onValueChange={(value) => {
                                const hours = parseInt(value)
                                updateDayGroup(index, { 
                                  breakHours: { ...group.breakHours, hours } 
                                })
                              }}
                            >
                              <SelectTrigger className="h-9 text-xs">
                                <SelectValue placeholder="Hrs" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 9 }, (_, i) => (
                                  <SelectItem key={i} value={i.toString()}>
                                    {i} {i === 1 ? 'hour' : 'hours'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex-1">
                            <Select 
                              value={group.breakHours.minutes.toString()} 
                              onValueChange={(value) => {
                                const minutes = parseInt(value)
                                updateDayGroup(index, { 
                                  breakHours: { ...group.breakHours, minutes } 
                                })
                              }}
                            >
                              <SelectTrigger className="h-9 text-xs">
                                <SelectValue placeholder="Min" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0 min</SelectItem>
                                <SelectItem value="15">15 min</SelectItem>
                                <SelectItem value="30">30 min</SelectItem>
                                <SelectItem value="45">45 min</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {(() => {
                            const { hours, minutes } = group.breakHours
                            if (hours === 0 && minutes === 0) {
                              return "No break time"
                            } else if (hours === 0) {
                              return `Total break: ${minutes} minute${minutes !== 1 ? 's' : ''}`
                            } else if (minutes === 0) {
                              return `Total break: ${hours} hour${hours !== 1 ? 's' : ''}`
                            } else {
                              return `Total break: ${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`
                            }
                          })()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
        

      </div>
      
      <DialogFooter className="pt-4 px-4">
        <Button variant="outline" onClick={onCancel} disabled={isSaving} className="h-9">
          Cancel
        </Button>
        <Button 
          onClick={() => saveShift()} 
          variant="secondary"
          disabled={isSaving || !getCurrentValidation().isValid || !category.trim() || !shiftName.trim()} 
          className="h-9"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {shift ? "Updating..." : "Adding..."}
            </>
          ) : (
            shift ? "Update Shift" : "Add Shift"
          )}
        </Button>
      </DialogFooter>
    </>
  )
}
